import { isExternalUrl } from "./cookieConsentUtils";
import { getViewerCopy } from "./viewerLanguage";
import { blockedContentCopy, BLOCKED_ICON_MARKUP } from "./blockedContentCopy";

export const VIDEO_HOSTS = /youtube\.com|youtu\.be|vimeo\.com|wistia\.(com|net)|dailymotion\.com|loom\.com|vidyard\.com/i;
export const AUDIO_HOSTS = /soundcloud\.com|spotify\.com/i;

const mediaSources = (node) => [
  node.getAttribute("src"),
  node.getAttribute("poster"),
  ...Array.from(node.querySelectorAll("source, track")).map((child) => child.getAttribute("src")),
].filter(Boolean);

const kindFor = (node) => {
  if (node.tagName === "IMG") return "image";
  if (node.tagName === "AUDIO") return "audio";
  if (node.tagName === "VIDEO") return "video";
  const src = node.getAttribute("src") || "";
  if (VIDEO_HOSTS.test(src)) return "video";
  if (AUDIO_HOSTS.test(src)) return "audio";
  return "content";
};

const STEPS_MIN_WIDTH = 200;
const STEPS_MIN_HEIGHT = 140;

const appendText = (doc, parent, tag, className, text) => {
  const node = doc.createElement(tag);
  node.className = className;
  node.textContent = text;
  parent.appendChild(node);
  return node;
};

const buildPlaceholder = (doc, node) => {
  const copy = blockedContentCopy(getViewerCopy()?.consent, kindFor(node), mediaSources(node)[0]);
  const width = node.getAttribute("width");
  const height = node.getAttribute("height");
  const placeholder = doc.createElement("span");
  placeholder.className = "kz-embed-blocked";
  placeholder.setAttribute("data-kz-cookie-settings", "true");
  placeholder.setAttribute("role", "button");
  placeholder.setAttribute("tabindex", "0");
  placeholder.setAttribute("title", copy.hint);
  const roomForSteps = parseFloat(width) >= STEPS_MIN_WIDTH && parseFloat(height) >= STEPS_MIN_HEIGHT;
  const sizing = [
    width ? `width:${/^\d+$/.test(width) ? `${width}px` : width}` : "",
    height ? `${roomForSteps ? "min-height" : "height"}:${/^\d+$/.test(height) ? `${height}px` : height}` : "",
  ].filter(Boolean).join(";");
  if (sizing) placeholder.setAttribute("style", sizing);
  const icon = doc.createElement("span");
  icon.className = "kz-embed-blocked__icon";
  icon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="#5a7fa8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${BLOCKED_ICON_MARKUP[copy.kind]}</svg>`;
  placeholder.appendChild(icon);
  appendText(doc, placeholder, "span", "kz-embed-blocked__title", copy.title);
  if (roomForSteps) {
    placeholder.classList.add("kz-embed-blocked--steps");
    appendText(doc, placeholder, "span", "kz-embed-blocked__hint", copy.hint);
    const steps = doc.createElement("span");
    steps.className = "kz-embed-blocked__steps";
    appendText(doc, steps, "span", "kz-embed-blocked__steps-title", copy.stepsTitle);
    copy.steps.forEach((step, index) => appendText(doc, steps, "span", "kz-embed-blocked__step", `${index + 1}. ${step}`));
    placeholder.appendChild(steps);
  }
  appendText(doc, placeholder, "span", "kz-embed-blocked__action", copy.button);
  return placeholder;
};

const stripExternalBackgrounds = (style) =>
  style.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, quote, url) =>
    isExternalUrl(url) ? "none" : match
  );

export const gateThirdPartyHtml = (html) => {
  if (!html || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return html;

  root.querySelectorAll("iframe, video, audio").forEach((node) => {
    const sources = mediaSources(node);
    if (node.tagName === "IFRAME" || sources.some(isExternalUrl)) {
      node.replaceWith(buildPlaceholder(doc, node));
    }
  });

  root.querySelectorAll("img").forEach((node) => {
    if (isExternalUrl(node.getAttribute("src"))) node.replaceWith(buildPlaceholder(doc, node));
  });

  root.querySelectorAll("[style]").forEach((node) => {
    const style = node.getAttribute("style");
    if (/url\(/i.test(style)) node.setAttribute("style", stripExternalBackgrounds(style));
  });

  return root.innerHTML;
};
